/**
 * OAuth Popup Utilities
 *
 * Shared utility for opening OAuth popups synchronously to avoid browser blocking.
 * Must be called directly from user gesture handlers (button clicks), NOT after any await.
 *
 * SSR-safe: Returns null if window is unavailable (server-side rendering).
 */

import type { ProviderKey } from '@/types/sync';

/**
 * Loading HTML content for the OAuth popup.
 * Shows a spinner while the Edge Function fetches the OAuth URL.
 */
const OAUTH_POPUP_LOADING_HTML = `
<!DOCTYPE html>
<html>
  <head>
    <title>Connecting to Google...</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100vh;
        margin: 0;
        background: #f5f5f5;
        color: #333;
      }
      .spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #e0e0e0;
        border-top-color: #4285f4;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-bottom: 16px;
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      p { margin: 0; font-size: 16px; }
    </style>
  </head>
  <body>
    <div class="spinner"></div>
    <p>Connecting to Google...</p>
  </body>
</html>
`;

/**
 * Opens an OAuth popup synchronously to preserve user gesture context.
 *
 * **CRITICAL**: Must be called directly from a button click handler, NOT after any await.
 * Browsers block popups that are opened too far from the user gesture origin.
 *
 * @param providerKey - The provider requiring OAuth (only 'google-drive' needs a popup)
 * @returns Window reference if opened successfully, null if:
 *   - Provider doesn't require OAuth (e.g., 'local' directory)
 *   - Popup was blocked by the browser
 *
 * @remarks
 * **Caller Responsibilities:**
 * - Callers MUST check for null return when `providerKey === 'google-drive'`
 * - If null for google-drive, show user-facing message: "Please enable popups for this site"
 * - Do NOT proceed with OAuth flow if popup is null for cloud providers
 * - Local directory callers can safely ignore null (no popup needed)
 *
 * **Implementation Notes:**
 * - Window name 'GoogleOAuth' ensures retries reuse the same popup window
 * - Loading HTML is written immediately; do NOT touch popup after this (cross-origin)
 * - Checking `popup.closed` on cross-origin windows may trigger COOP/COEP console warnings
 *   (these warnings are harmless and expected)
 *
 * @example
 * ```typescript
 * const handleConnect = () => {
 *   // Open popup FIRST (synchronous, preserves user gesture)
 *   const popup = openOAuthPopup('google-drive');
 *
 *   // Guard: popup blocked for cloud provider
 *   if (!popup) {
 *     setPopupBlocked(true);
 *     return;
 *   }
 *
 *   // Proceed with OAuth flow
 *   connect('google-drive', { oauthPopup: popup });
 * };
 * ```
 */
export function openOAuthPopup(providerKey: ProviderKey): Window | null {
  // SSR guard: return null if window is unavailable
  if (typeof window === 'undefined') {
    return null;
  }

  // Only google-drive requires OAuth popup
  if (providerKey !== 'google-drive') {
    return null;
  }

  // Open blank popup immediately to preserve user gesture context
  // Browser will block popup if opened after async work
  const popup = window.open('about:blank', 'GoogleOAuth', 'width=600,height=700');

  // Write loading indicator to popup (shown while Edge Function fetches OAuth URL)
  // After this, do NOT touch the popup - cross-origin navigation will replace it
  if (popup) {
    popup.document.write(OAUTH_POPUP_LOADING_HTML);
    popup.document.close();
  }

  return popup;
}
